import { useState } from "react"
import { Box, Tooltip, Typography } from "@mui/material"
import {
  useGeoServerLegend,
  convertLegendValue,
  getLegendUnitOptions,
  getDefaultLegendUnit,
} from "@geoflow/benchmap"
import { WW_BLUE, PANEL_TEXT_MUTED } from "@/theme/tokens"

// Ticks, die auf dem Balken näher zusammenrücken würden, fallen weg
const MIN_TICK_GAP_PCT = 4.5
// Zielzahl für den automatischen Schritt (Einheiten ohne konfigurierte Ticks)
const MAX_AUTO_TICKS = 6
// Pill und Farbbalken exakt gleich hoch, oben bündig
const BAR_HEIGHT = "20px"

// Style-Labels wie "Bft - calm" verunreinigen die extrahierte Einheit
const sanitizeSourceUnit = (unit) => unit?.split(/[\s-]/)[0]

// source muss die native Einheit der Quantities sein — die Label-Heuristik
// rät bei verbosen Style-Texten falsch und die Konvertierung rechnet dann
// mit der falschen Basis
export const SCENE_LEGEND_CONFIG = {
  wind: {
    source: "m/s",
    display: "kts",
    stepByUnit: { "m/s": 5, "km/h": 20, kts: 10 },
  },
  waves: { source: "m", display: "m", stopTicks: true },
  current: {
    source: "m/s",
    display: "kts",
    stopTicks: true,
    excludeUnits: ["bft"],
    ticksByUnit: {
      kts: [0, 0.1, 0.2, 0.5, 1, 2, 3, 4],
      "km/h": [0, 0.2, 0.4, 1, 2, 4, 5, 6, 7],
    },
  },
}

// Eigenes Rendering, weil benchmaps LegendScale auf fixer 246px-Bar überlappt;
// Fetch/Parse/Unit-Konvertierung kommen weiter aus dem Paket
export default function WeatherLegend({ legendUrl, config = {} }) {
  const { legend: rawLegend } = useGeoServerLegend(legendUrl)
  const [unitOverride, setUnitOverride] = useState(null)

  if (!rawLegend || rawLegend.kind !== "ramp" || rawLegend.entries.length < 2) {
    return null
  }

  const {
    source,
    display,
    stepByUnit = {},
    stopTicks = false,
    ticksByUnit = {},
    excludeUnits = [],
  } = config

  const legend = {
    ...rawLegend,
    unit: source ?? sanitizeSourceUnit(rawLegend.unit),
  }
  // z.B. kein bft bei Strömung — Beaufort ist eine Windskala
  const unitOptions = getLegendUnitOptions(legend.unit).filter(
    (option) => !excludeUnits.includes(option),
  )
  const displayUnit =
    [unitOverride, display, getDefaultLegendUnit(legend.unit)].find(
      (candidate) => candidate && unitOptions.includes(candidate),
    ) ?? legend.unit
  // convertLegendModel konvertiert nur Labels, nicht entry.value — daher
  // die Werte selbst umrechnen (unbekannte Paare lässt benchmap unverändert)
  const displayValue = (entry) =>
    displayUnit && displayUnit !== legend.unit
      ? convertLegendValue(entry.value, legend.unit, displayUnit)
      : entry.value
  const { entries } = legend

  const gradient = entries
    .map((entry, i) => `${entry.color} ${(i / (entries.length - 1)) * 100}%`)
    .join(", ")

  const values = entries.map(displayValue)
  const minValue = values[0]
  const maxValue = values[values.length - 1]

  // Dieselbe stückweise Interpolation, mit der die Ramp die Kartenfarben
  // mischt — der Tick trifft also exakt die Farbe seines Werts
  const valueToPercent = (v) => {
    for (let i = 0; i < values.length - 1; i++) {
      if (v <= values[i + 1]) {
        const span = values[i + 1] - values[i]
        const fraction = span > 0 ? (v - values[i]) / span : 0
        return Math.min(100, Math.max(0, ((i + fraction) / (values.length - 1)) * 100))
      }
    }
    return 100
  }

  const ticks = []
  let lastPercent = -Infinity
  const pushTick = (value, minGap = MIN_TICK_GAP_PCT) => {
    const percent = valueToPercent(value)
    if (percent - lastPercent < minGap) return
    ticks.push({ value, percent })
    lastPercent = percent
  }

  // Handverlesene Werte; über dem Skalenende klemmen sie auf 100% (nominale
  // Obergrenzen wie "4 kn" bei 3.89) — halber Mindestabstand gegen Stapelung
  const explicitTicks = ticksByUnit[displayUnit]
  if (explicitTicks) {
    for (const v of explicitTicks) {
      if (v >= minValue - 1e-9) pushTick(v, MIN_TICK_GAP_PCT / 2)
    }
  }
  if (ticks.length === 0 && stopTicks) {
    // Klassengrenzen, auf 2 Nachkommastellen dedupliziert
    const seen = new Set()
    for (const v of values) {
      const rounded = Math.round(v * 100) / 100
      if (seen.has(rounded)) continue
      seen.add(rounded)
      pushTick(rounded)
    }
  } else if (ticks.length === 0) {
    let step = stepByUnit[displayUnit]
    if (step == null) {
      // Automatischer 1/2/5×10^k-Schritt (aktuell nur Wind in bft)
      const rawStep = (maxValue - minValue) / MAX_AUTO_TICKS
      const pow = Math.pow(10, Math.floor(Math.log10(rawStep || 1)))
      step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= rawStep)
    }
    if (step > 0) {
      for (
        let v = Math.ceil(minValue / step) * step;
        v <= maxValue + 1e-9;
        v += step
      ) {
        pushTick(v)
      }
    }
  }

  const canCycleUnit = unitOptions.length > 1
  const cycleUnit = () => {
    const i = unitOptions.indexOf(displayUnit)
    setUnitOverride(unitOptions[(i + 1) % unitOptions.length])
  }

  return (
    <Box id="weather-legend" data-tour="weather-legend" sx={{ display: "flex", alignItems: "flex-start", gap: "8px", mt: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            height: BAR_HEIGHT,
            borderRadius: "999px",
            background: `linear-gradient(to right, ${gradient})`,
            boxShadow: "inset 0 0 0 1px rgba(20,30,45,0.08)",
          }}
        />
        <Box sx={{ position: "relative", height: "11px", mt: "2px" }}>
          {ticks.map((tick) => (
            <Typography
              key={tick.value}
              sx={{
                position: "absolute",
                left: `${tick.percent}%`,
                transform: "translateX(-50%)",
                fontSize: "10px",
                lineHeight: 1,
                color: PANEL_TEXT_MUTED,
              }}
            >
              {String(Math.round(tick.value * 100) / 100)}
            </Typography>
          ))}
        </Box>
      </Box>
      {displayUnit && (
        <Tooltip title={canCycleUnit ? "Change unit" : ""} placement="top">
          <Box
            component={canCycleUnit ? "button" : "div"}
            type={canCycleUnit ? "button" : undefined}
            onClick={canCycleUnit ? cycleUnit : undefined}
            sx={{
              flexShrink: 0,
              height: BAR_HEIGHT,
              display: "inline-flex",
              alignItems: "center",
              p: "0 10px",
              borderRadius: "999px",
              border: "1px solid #e2e8ee",
              bgcolor: "#fff",
              fontSize: "11px",
              fontWeight: 700,
              fontFamily: "inherit",
              color: WW_BLUE,
              cursor: canCycleUnit ? "pointer" : "default",
            }}
          >
            {displayUnit}
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}
